import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AdminShell } from "@/components/admin/AdminShell";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { useProducts } from "@/context/ProductsContext";
import { api, apiError } from "@/lib/api";
import { CATEGORIES } from "@/data/products";

const EMPTY = { name: "", short: "", category: CATEGORIES[1], isNew: false, swatch: "#FF5A00", image: "", tagline: "", description: "", applications: "", features: "" };
const input = "mt-2 w-full border border-black/20 bg-white px-4 py-3 text-base outline-none transition-colors focus:border-[#FF5A00]";

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-700">{label}</span>
    {children}
    {hint && <span className="mt-1.5 block text-xs text-zinc-600">{hint}</span>}
  </label>
);

const toForm = (p) => ({ ...EMPTY, ...p, applications: (p.applications || []).join("\n"), features: (p.features || []).join("\n") });
const toBody = (f) => ({
  name: f.name.trim(), short: (f.short || f.name).trim(), category: f.category, isNew: f.isNew, swatch: f.swatch, image: f.image,
  tagline: f.tagline.trim(), description: f.description.trim(),
  applications: f.applications.split("\n"), features: f.features.split("\n"),
});

export default function AdminProductForm() {
  const { slug } = useParams();
  const editing = Boolean(slug);
  const nav = useNavigate();
  const { refresh } = useProducts();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));

  useEffect(() => {
    if (!editing) return undefined;
    let alive = true;
    api.get(`/products/${slug}`)
      .then(({ data }) => { if (alive) setForm(toForm(data)); })
      .catch((e) => { toast.error(apiError(e)); nav("/admin", { replace: true }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [editing, slug, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image) { toast.error("Please upload a product photo"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/products/${slug}`, toBody(form));
      else await api.post("/admin/products", toBody(form));
      await refresh();
      toast.success(editing ? "Product updated" : "Product added");
      nav("/admin");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const back = <Link to="/admin" data-testid="admin-form-back" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-zinc-700 hover:text-[#0B0B0B]"><ArrowLeft className="h-4 w-4" /> All products</Link>;

  if (loading) return <AdminShell eyebrow="Loading" title="Edit product" actions={back}><Loader2 className="h-6 w-6 animate-spin" /></AdminShell>;

  return (
    <AdminShell eyebrow={editing ? `Editing · /${slug}` : "New product"} title={editing ? "Edit product" : "Add product"} actions={back}>
      <form onSubmit={submit} className="grid gap-10 lg:grid-cols-12" data-testid="admin-product-form">
        <div className="lg:col-span-4">
          <ImageUpload value={form.image} onChange={set("image")} />
        </div>
        <div className="space-y-6 lg:col-span-8">
          <div className="grid gap-6 sm:grid-cols-2">
            <Field label="Product name *"><input required minLength={2} value={form.name} onChange={set("name")} data-testid="admin-field-name" className={input} placeholder="e.g. Heat Resistant Inks for Cement Bags" /></Field>
            <Field label="Short name" hint="Used in lists. Leave empty to use the full name."><input value={form.short} onChange={set("short")} data-testid="admin-field-short" className={input} /></Field>
            <Field label="Category *">
              <select value={form.category} onChange={set("category")} data-testid="admin-field-category" className={input}>
                {CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Colour dot" hint="Small accent colour shown next to the product.">
              <div className="mt-2 flex items-center gap-3">
                <input type="color" value={form.swatch} onChange={set("swatch")} data-testid="admin-field-swatch" className="h-12 w-16 cursor-pointer border border-black/20 bg-white" />
                <span className="font-mono text-sm">{form.swatch}</span>
              </div>
            </Field>
          </div>
          <Field label="Tagline" hint="One short line shown under the name."><input value={form.tagline} onChange={set("tagline")} maxLength={160} data-testid="admin-field-tagline" className={input} /></Field>
          <Field label="Description"><textarea rows={4} value={form.description} onChange={set("description")} data-testid="admin-field-description" className={input} /></Field>
          <div className="grid gap-6 sm:grid-cols-2">
            <Field label="Applications" hint="One per line."><textarea rows={4} value={form.applications} onChange={set("applications")} data-testid="admin-field-applications" className={input} /></Field>
            <Field label="Key features" hint="One per line."><textarea rows={4} value={form.features} onChange={set("features")} data-testid="admin-field-features" className={input} /></Field>
          </div>
          <label className="flex cursor-pointer items-center gap-3">
            <input type="checkbox" checked={form.isNew} onChange={set("isNew")} data-testid="admin-field-isnew" className="h-5 w-5 accent-[#FF5A00]" />
            <span className="text-base">Mark as <b>New</b> (shows in the "New range" section on the home page)</span>
          </label>
          <div className="flex flex-wrap gap-3 border-t border-black/10 pt-6">
            <button type="submit" disabled={saving} data-testid="admin-save-btn" className="flex items-center gap-2 bg-[#0B0B0B] px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] text-white hover:bg-[#FF5A00] disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save changes" : "Add product"}
            </button>
            <Link to="/admin" data-testid="admin-cancel-btn" className="border border-black/20 px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] hover:border-[#0B0B0B]">Cancel</Link>
          </div>
        </div>
      </form>
    </AdminShell>
  );
}
